import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TextField,
  TableRow,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSingleStaff, createVirtualTransaction, deleteVirtualTransaction } from '../slices/staffSlice';
import { fetchEmployeeTypes } from '../slices/employeeTypesSlice';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import BadgeIcon from '@mui/icons-material/Badge';
import ArchiveIcon from '@mui/icons-material/Archive';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { startOfMonth, endOfMonth } from 'date-fns';
import { exportToExcel, exportToPrintWindow, exportStaffDetailsToPrint } from '../utils/exportUtils';
import axios from '../api/axiosInstance';
import EmployeeAbsenceSection from '../components/staff/EmployeeAbsenceSection';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

// Stable empty-array reference so destructuring defaults don't create a new
// array identity on every render (which previously caused an infinite effect loop).
const EMPTY_ARRAY = [];

const StaffFinancePage = () => {
  const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
]
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [openModal, setOpenModal] = useState(false);
  const today = new Date();

  // FIXED: Date range inputs - from 1st to last day of current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format dates without timezone conversion
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      first: formatDate(firstDay),
      last: formatDate(lastDay)
    };
  };

  const currentMonth = getCurrentMonthDates();
  
  const [fromDate, setFromDate] = useState(currentMonth.first);
  const [fromDateDialog, setFromDateDialog] = useState(currentMonth.first);
  const [toDate, setToDate] = useState(currentMonth.last);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [combinedRows, setCombinedRows] = useState([]);
  const [openTransactionForm, setOpenTransactionForm] = useState(false);
  
  // Salary Exception states
  const [openExceptionForm, setOpenExceptionForm] = useState(false);
  const [exceptionYear, setExceptionYear] = useState(new Date().getFullYear());
  const [exceptionMonth, setExceptionMonth] = useState(new Date().getMonth() + 1);
  const [exceptionType, setExceptionType] = useState('unpaid_leave');
  const [exceptionReason, setExceptionReason] = useState('');
  
  const employeeId = id;
  const staffCurrent = useSelector((state) => state?.staff?.current) || {};
  const payments = staffCurrent.payments || EMPTY_ARRAY;
  const virtuals = staffCurrent.virtual_transactions || EMPTY_ARRAY;
  const [selectedSalary, setSelectedSalary] = useState(null);
  const dispatch = useDispatch();
  const staff = useSelector((state) => state.staff.current);
  const employeeTypes = useSelector((state) => state.employeeTypes.items);
  const { deleteTransactionError } = useSelector((state) => state.staff);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const baseSalary = parseFloat(staff?.base_salary || 0);
    const filterStart = new Date(fromDate);
    const filterEnd = new Date(toDate);
    
    // Get employee start date
    const employeeStartDate = staff?.start_date ? new Date(staff.start_date) : null;
    
    // Get salary exceptions (months where employee shouldn't receive salary)
    const salaryExceptions = staff?.salary_exceptions || [];
    
    const baseSalaryRows = [];

    // Generate base salary for each month between fromDate and toDate
    const current = new Date(filterStart.getFullYear(), filterStart.getMonth(), 1);
    
    while (current <= filterEnd) {
      const currentYear = current.getFullYear();
      const currentMonth = current.getMonth() + 1;
      
      // Check if this month has a salary exception
      const hasException = salaryExceptions.some(exception => 
        exception.year === currentYear && exception.month === currentMonth
      );
      
      // Only add salary if:
      // 1. Current month is after or equal to employee start date
      // 2. No salary exception exists for this month
      if ((!employeeStartDate || current >= new Date(employeeStartDate.getFullYear(), employeeStartDate.getMonth(), 1)) && !hasException) {
        const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        baseSalaryRows.push({
          date: dateStr,
          voucherNumber: '',
          credit: baseSalary,
          debit: 0,
          description: 'راتب أساسي',
          type: 'salary'
        });
      }
      
      current.setMonth(current.getMonth() + 1);
    }

    const merged = [
      ...baseSalaryRows,
      ...virtuals.map(v => {
        const amount = parseFloat(v.amount);
        const isDebit = v.direction === 'debit';
        return {
          date: v.date,
          voucherNumber: '',
          credit: isDebit ? 0 : amount,
          debit: isDebit ? amount : 0,
          description: v.reason || v.type,
          type: 'virtual',
          transactionId: v.id
        };
      }),
      ...payments?.map(p => ({
        date: p.date,
        voucherNumber: p.number,
        credit: 0,
        debit: parseFloat(p.amount),
        description: p.reason,
        type: 'payment'
      }))
    ];

    const sorted = merged.sort((a, b) => new Date(a.date) - new Date(b.date));
    setCombinedRows(sorted);
  }, [payments, virtuals, staff, fromDate, toDate]);

  const filteredRows = useMemo(() => {
    return combinedRows.filter(row => {
      const date = new Date(row.date);
      return new Date(fromDate) <= date && date <= new Date(toDate);
    });
  }, [combinedRows, fromDate, toDate]);

  // FIXED: Calculate totals from filteredRows instead of combinedRows
  const { totalCredit, totalDebit, net } = useMemo(() => {
    const credit = filteredRows.reduce((sum, tx) => sum + (tx.credit || 0), 0);
    const debit = filteredRows.reduce((sum, tx) => sum + (tx.debit || 0), 0);
    const netAmount = credit - debit;
    
    return {
      totalCredit: credit,
      totalDebit: debit,
      net: netAmount
    };
  }, [filteredRows]);

  const filteredSalaries = staff?.salaryHistory?.filter((record) => {
    const recordDate = new Date(record.date);
    return new Date(fromDate) <= recordDate && recordDate <= new Date(toDate);
  }) || [];

  // Function to reset to current month
  const resetToCurrentMonth = () => {
    const dates = getCurrentMonthDates();
    console.log('Resetting to current month:', dates);
    
    setFromDate(dates.first);
    setToDate(dates.last);
  };

  // Handle delete virtual transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المعاملة؟')) {
      try {
        await dispatch(deleteVirtualTransaction(transactionId)).unwrap();
        showToast('تم حذف المعاملة بنجاح', 'success');
      } catch (error) {
        showToast(error?.message || 'فشل في حذف المعاملة', 'error');
      }
    }
  };

  // Salary Exception handlers
  const handleCreateException = async () => {
    try {
      const response = await axios.post(
        `api/employees/${staff.id}/salary-exceptions/`,
        {
          year: exceptionYear,
          month: exceptionMonth,
          exception_type: exceptionType,
          reason: exceptionReason
        }
      );
      
      showToast('تم إضافة استثناء الراتب بنجاح', 'success');
      setOpenExceptionForm(false);
      setExceptionReason('');
      // Refresh staff data
      dispatch(fetchSingleStaff(staff.id));
    } catch (error) {
      showToast('فشل في إضافة الاستثناء', 'error');
    }
  };

  const handleDeleteException = async (exceptionId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الاستثناء؟')) {
      try {
        await axios.delete(`/employees/salary-exceptions/${exceptionId}/`);
        showToast('تم حذف الاستثناء بنجاح', 'success');
        dispatch(fetchSingleStaff(staff.id));
      } catch (error) {
        showToast('فشل في حذف الاستثناء', 'error');
      }
    }
  };

  useEffect(() => {
    dispatch(fetchEmployeeTypes());
    dispatch(fetchSingleStaff(id))
      .unwrap()
      .catch(() => {
        showToast('الموظف غير موجود', 'error');
        navigate('/staff');
      });
  }, [dispatch, id, navigate, showToast]);

  const getEmployeeTypeName = (id, types) => {
    const match = types.find(t => t.id === id);
    return match?.display_value || 'غير محدد';
  };

  // Add these helper functions **inside** StaffFinancePage component, before the return statement:
  const handleExportExcel = () => {
    const columns = [
      { header: 'التاريخ', key: 'date' },
      { header: 'رقم السند', key: 'voucherNumber' },
      { header: 'له (شيكل)', key: 'credit' },
      { header: 'عليه (شيكل)', key: 'debit' },
      { header: 'الوصف', key: 'description' },
    ];
    const data = filteredRows.map((tx) => ({
      date: tx.date,
      voucherNumber: tx.voucherNumber,
      credit: tx.credit,
      debit: tx.debit,
      description: tx.description,
    }));
    exportToExcel(data, columns, `staff_finance_${staff?.first_name || ''}_${staff?.last_name || ''}`);
  };

  const handlePrint = () => {
    exportStaffDetailsToPrint(staff, {
      transactions: filteredRows,
      totalCredit,
      totalDebit,
      net,
    });
  };

  if (!staff) return null;

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AttachMoneyIcon sx={{ color: '#fff', fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="#fff"
              sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              الملف المالي للموظف
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                mt: 0.25,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              {`${staff.first_name || ''} ${staff.last_name || ''}`.trim() || 'إدارة رواتب وحركات الموظف المالية'}
            </Typography>
          </Box>
        </Stack>

        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          {staff.is_archived && (
            <Chip
              icon={<ArchiveIcon sx={{ color: '#DC2626 !important' }} />}
              label="غير نشط"
              sx={{
                fontWeight: 700,
                px: 1,
                bgcolor: '#fff',
                color: '#DC2626',
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            />
          )}
          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/staff')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            الرجوع إلى الموظفين
          </Button>
        </Stack>
      </MotionPaper>

      {/* Main Info Card */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          mb: 3,
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: '#1E1B4B', mb: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
        >
          البيانات الشخصية
        </Typography>
        <Stack spacing={2} dir="rtl">
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <PeopleIcon color="primary" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>الاسم الكامل:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{`${staff.first_name || ''} ${staff.last_name || ''}`}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <BadgeIcon color="info" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الهوية:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{staff.employee_id || '—'}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <PhoneIcon color="success" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>الهاتف:</Typography>
            <Typography fontWeight="bold">
              {staff.phone_number ? `${staff.phone_number} ${staff.phone_code || '972'}` : '—'}
            </Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <EmailIcon color="secondary" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>البريد الإلكتروني:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{staff.email || '—'}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <CalendarMonthIcon color="primary" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>تاريخ الميلاد:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{staff.birth_date || '—'}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <CalendarMonthIcon color="primary" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>تاريخ التعيين:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{staff.start_date || '—'}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <InfoIcon color="info" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>القسم/نوع الموظف:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{getEmployeeTypeName(staff.employee_type, employeeTypes)}</Typography>
          </Stack>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <InfoIcon color="info" />
            <Typography variant="subtitle2" sx={{ minWidth: 120, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>ملاحظات:</Typography>
            <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{staff.note || 'لا توجد ملاحظات'}</Typography>
          </Stack>
        </Stack>
      </MotionPaper>

      {/* Salary Card */}
      <>
      <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'الراتب الأساسي',
            value: `${Number(staff.base_salary || 0).toLocaleString('en-US')} شيكل`,
            icon: <AttachMoneyIcon />,
            fg: '#4338CA',
            bg: '#EEF2FF',
            delay: 0.1,
          },
          {
            label: 'إجمالي له',
            value: `${totalCredit.toLocaleString('en-US')} شيكل`,
            icon: <TrendingUpIcon />,
            fg: '#16A34A',
            bg: '#DCFCE7',
            delay: 0.15,
          },
          {
            label: 'إجمالي عليه',
            value: `${totalDebit.toLocaleString('en-US')} شيكل`,
            icon: <TrendingDownIcon />,
            fg: '#DC2626',
            bg: '#FEE2E2',
            delay: 0.2,
          },
          {
            label: 'الصافي للموظف',
            value: `${net.toLocaleString('en-US')} شيكل`,
            icon: <AccountBalanceWalletIcon />,
            fg: net >= 0 ? '#0284C7' : '#DC2626',
            bg: net >= 0 ? '#E0F2FE' : '#FEE2E2',
            delay: 0.25,
          },
        ].map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <MotionPaper
              component={motion.div}
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: card.delay, ease: 'easeOut' }}
              whileHover={{ y: -4 }}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  bgcolor: card.bg,
                  color: card.fg,
                  borderRadius: 2.5,
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    color: '#1E1B4B',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    lineHeight: 1.2,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  {card.label}
                </Typography>
              </Box>
            </MotionPaper>
          </Grid>
        ))}
      </Grid>

      {/* Salary Exceptions Card */}
      {staff?.salary_exceptions && staff.salary_exceptions.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            mb: 3,
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#D97706', mb: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            استثناءات الراتب (أشهر بدون راتب)
          </Typography>
          <Stack spacing={1.5}>
            {staff.salary_exceptions.map((exception) => (
              <Stack
                key={exception.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  p: 2,
                  bgcolor: '#FEF3C7',
                  borderRadius: 2.5,
                  border: '1px solid #FDE68A'
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {exception.month}/{exception.year}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {exception.exception_type_display}: {exception.reason}
                  </Typography>
                </Box>
                <Tooltip title="حذف الاستثناء">
                  <IconButton
                    onClick={() => handleDeleteException(exception.id)}
                    size="small"
                    sx={{
                      color: '#DC2626',
                      '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Employee Absence Section - shows absent days per year with ability to discount from salary */}
      <EmployeeAbsenceSection employeeId={employeeId} />

      {/* Salary History List */}
      {staff.salaryHistory && staff.salaryHistory.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            mb: 3,
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', mb: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>سجل الرواتب</Typography>
          <Stack useFlexGap direction="row" spacing={1.5} flexWrap="wrap">
            {filteredSalaries.map((record, idx) => (
              <Button
                key={idx}
                variant="outlined"
                sx={{
                  borderRadius: 2.5,
                  minWidth: 160,
                  mb: 1,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  bgcolor: '#fff',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
                onClick={() => setOpenModal(record)}
              >
                راتب شهر {record.date}
              </Button>
            ))}
          </Stack>
        </Paper>
      )}

      <Stack direction="row" justifyContent="center" mt={3}>
        <Button
          variant="contained"
          startIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: 2.5,
            px: 4,
            py: 1.1,
            fontWeight: 700,
            textTransform: 'none',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            bgcolor: '#4338CA',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
          }}
          onClick={() => navigate('/staff')}
        >
          الرجوع إلى جدول الموظفين
        </Button>
      </Stack>
      </Box>

      {/* Salary Details Modal */}
      </>
      )}

      {/* Dialogs - Always visible */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تفاصيل الراتب</DialogTitle>
        <DialogContent>
          {selectedSalary && (
            <Stack spacing={2} mt={2}>
              <Typography>🗓️ الشهر: {selectedSalary.date}</Typography>
              <Typography>💵 الراتب الأساسي: {selectedSalary.baseSalary} شيكل</Typography>
              <Typography>⏱️ الساعات الإضافية: {selectedSalary.overtimeHours} × {selectedSalary.overtimeRate} = {selectedSalary.overtimeHours * selectedSalary.overtimeRate} شيكل</Typography>
              <Divider sx={{ my: 2 }} />
              <Typography>🎁 الحوافز:</Typography>
              {selectedSalary.bonuses && selectedSalary.bonuses.length > 0 ? (
                selectedSalary.bonuses.map((bonus, idx) => (
                  <Typography key={idx}>{bonus.type}: {bonus.amount} شيكل</Typography>
                ))
              ) : (
                <Typography color="text.secondary">لا توجد حوافز</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#16A34A', fontWeight: 800, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                💰 الراتب الإجمالي: {
                  selectedSalary.baseSalary +
                  (selectedSalary.overtimeHours * selectedSalary.overtimeRate) +
                  (selectedSalary.bonuses ? selectedSalary.bonuses.reduce((sum, bonus) => sum + bonus.amount, 0) : 0)
                } شيكل
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Form Dialog */}
      <Dialog
        open={openTransactionForm}
        onClose={() => setOpenTransactionForm(false)}
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إضافة حافز / مكافأة</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={2}>
            <TextField
              label="التاريخ"
              type="date"
              value={fromDateDialog}
              onChange={(e) => setFromDateDialog(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="المبلغ"
              type="number"
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <TextField
              label="النوع / السبب"
              onChange={(e) => setReason(e.target.value)}
            />
            {submitError && (
              <Typography color="error" fontSize="0.9rem">
                {submitError}
              </Typography>
            )}
            <Button
              variant="contained"
              disabled={isSubmitting || !fromDateDialog || !amount || !reason}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
              onClick={async () => {
                setIsSubmitting(true);
                setSubmitError(null);
                try {
                  await dispatch(createVirtualTransaction({
                    employee: employeeId,
                    date: fromDateDialog,
                    amount,
                    reason,
                    type: 'مكافأة',
                    direction: 'credit'
                  })).unwrap();

                  showToast('تمت إضافة الحافز بنجاح', 'success');
                  setOpenTransactionForm(false);
                  setAmount('');
                  setReason('');
                  setFromDateDialog('');
                } catch (err) {
                  setSubmitError(err || 'فشل في الإرسال');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmitting ? 'جاري الإضافة...' : 'إضافة'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Salary Exception Form Dialog */}
      <Dialog
        open={openExceptionForm}
        onClose={() => setOpenExceptionForm(false)}
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إضافة شهر بدون راتب</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={2}>
            <TextField
              label="السنة"
              type="number"
              value={exceptionYear}
              onChange={(e) => setExceptionYear(parseInt(e.target.value))}
              inputProps={{ min: 2020, max: 2030 }}
            />
            <TextField
              label="الشهر"
              select
              value={exceptionMonth}
              onChange={(e) => setExceptionMonth(parseInt(e.target.value))}
              SelectProps={{ native: true }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i).toLocaleDateString('ar', { month: 'long' })}
                </option>
              ))}
            </TextField>
            <TextField
              label="نوع الاستثناء"
              select
              value={exceptionType}
              onChange={(e) => setExceptionType(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="unpaid_leave">إجازة بدون راتب</option>
              <option value="suspension">إيقاف</option>
              <option value="absence">غياب</option>
              <option value="custom">مخصص</option>
            </TextField>
            <TextField
              label="السبب"
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              multiline
              rows={2}
            />
            <Button
              variant="contained"
              disabled={!exceptionReason}
              onClick={handleCreateException}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#D97706',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#B45309', boxShadow: 'none' },
              }}
            >
              إضافة الاستثناء
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Employee Financial Transactions Table */}
      <>
      <Box sx={{ mt: 3 }}>
        {/* Toolbar: title + primary actions */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
          }}
        >
          <Stack useFlexGap
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              الحركات المالية للموظف
            </Typography>
            <Stack useFlexGap direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  px: 2,
                  bgcolor: '#4338CA',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                }}
                onClick={() =>
                  navigate('/finance/create', {
                    state: {
                      entityType: 'موظف',
                      targetId: staffCurrent.id,
                      targetName: `${staffCurrent.first_name} ${staffCurrent.last_name}`,
                    }
                  })
                }
              >
                إضافة سند صرف
              </Button>
              <Button
                variant="outlined"
                startIcon={<TrendingUpIcon />}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  px: 2,
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                }}
                onClick={() => setOpenTransactionForm(true)}
              >
                إضافة له (راتب/زيادة/محفز/مكافأة)
              </Button>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  px: 2,
                  borderColor: '#D97706',
                  color: '#D97706',
                  '&:hover': { borderColor: '#D97706', bgcolor: '#FEF3C7' },
                }}
                onClick={() => setOpenExceptionForm(true)}
              >
                إضافة شهر بدون راتب
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2, borderColor: '#E5E7EB' }} />

          {/* Filters + Export */}
          <Stack useFlexGap
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack useFlexGap direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
              <TextField
                label="من تاريخ"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="إلى تاريخ"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={resetToCurrentMonth}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  borderRadius: 2,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                }}
              >
                الشهر الحالي
              </Button>
            </Stack>

            <Stack useFlexGap direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  px: 2.5,
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                }}
                onClick={handleExportExcel}
              >
                تصدير Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  px: 2.5,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
                onClick={handlePrint}
              >
                طباعة
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Table Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 1.5, sm: 2 },
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
          }}
        >

          <TableContainer sx={{ borderRadius: 3, overflowX: 'auto', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>التاريخ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>رقم السند</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#16A34A', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>له (شيكل)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#DC2626', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>عليه (شيكل)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>الوصف</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '1px solid #E5E7EB' }}>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: '#6B7280', py: 4, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      لا توجد حركات مالية لهذا الموظف بعد
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((tx, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:hover': { bgcolor: '#F8F9FF' },
                        '& .MuiTableCell-root': { borderBottom: '1px solid #E5E7EB', fontFamily: 'Cairo, Tajawal, sans-serif', fontSize: '0.875rem' },
                      }}
                    >
                      <TableCell align="center" sx={{ color: '#1E1B4B' }}>{tx.date}</TableCell>
                      <TableCell align="center" sx={{ color: '#6B7280' }}>{tx.voucherNumber || '-'}</TableCell>
                      <TableCell align="center" sx={{ color: '#16A34A', fontWeight: 700 }}>
                        {tx.credit ? Number(tx.credit).toLocaleString('en-US') : ''}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#DC2626', fontWeight: 700 }}>
                        {tx.debit ? Number(tx.debit).toLocaleString('en-US') : ''}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#1E1B4B' }}>{tx.description || '-'}</TableCell>
                      <TableCell align="center">
                        {tx.type === 'virtual' && tx.transactionId && (
                          <Tooltip title="حذف المعاملة">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteTransaction(tx.transactionId)}
                              sx={{
                                color: '#DC2626',
                                '&:hover': {
                                  bgcolor: '#FEE2E2',
                                  color: '#DC2626'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {/* مجاميع */}
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    المجموع
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#16A34A', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {totalCredit.toLocaleString('en-US')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#DC2626', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {totalDebit.toLocaleString('en-US')}
                  </TableCell>
                  <TableCell colSpan={2} sx={{ bgcolor: '#F1F5F9' }} />
                </TableRow>

                {/* صافي */}
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#E0F2FE', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    الصافي للموظف
                  </TableCell>
                  <TableCell colSpan={4} align="center" sx={{ fontWeight: 800, color: net >= 0 ? '#0284C7' : '#DC2626', bgcolor: '#E0F2FE', fontSize: '1.05rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {net.toLocaleString('en-US')} شيكل
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      </>
      </Box>
    </Box>
  );
};

export default StaffFinancePage;